import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';
import useSEO from '../hooks/useSEO';

function WhitepaperStrategicSimulation({ logo }) {
  useSEO({
    title: 'Strategic Simulation for the Enterprise',
    description: 'Download the whitepaper on how simulation-driven decision intelligence transforms enterprise strategy.'
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await addDoc(collection(db, 'form-submissions-incremental'), {
        ...formData,
        source: 'whitepaper-download',
        page: '/innovation/strategic-simulation',
        whitepaper: 'Strategic Simulation for the Enterprise',
        intent: 'executive',
        submittedAt: serverTimestamp()
      });
      setSubmitted(true);
      // Trigger PDF download after form submission
      window.open('/veydra-whitepaper---strategic-simulation-for-the-enterprise.pdf', '_blank');
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <TopNav logo={logo} />

      <main className="max-w-6xl mx-auto px-8 py-16 md:py-24">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Link to="/innovation" className="text-sm text-gray-500 hover:text-gray-700">
            ← Back to Research & Innovation
          </Link>
        </div>

        {/* Two-column layout: Form left, Article right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Cover + Form (sticky) */}
          <div className="lg:col-span-4 lg:sticky lg:top-8">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
              <img 
                src="/veydra-whitepaper---strategic-simulation-for-the-enterprise.jpg" 
                alt="Strategic Simulation for the Enterprise whitepaper cover" 
                className="w-full rounded shadow-lg"
              />
            </div>

            {/* Download Form */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              {!submitted ? (
                <>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Download the Whitepaper</h3>
                  <p className="text-sm text-gray-600 mb-4">Fill out the form below to receive your copy.</p>
                  
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Full Name *"
                    />
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Work Email *"
                    />
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Company"
                    />
                    <input
                      type="text"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Role"
                    />
                    <button
                      type="submit"
                      className="w-full bg-gray-900 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                    >
                      Download Whitepaper →
                    </button>
                  </form>
                  {error && (
                    <p className="text-xs text-red-600 mt-2 text-center">{error}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    By downloading, you agree to receive occasional updates from Veydra.
                  </p>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Thank you!</h3>
                  <p className="text-sm text-gray-600 mb-4">Your download should begin automatically.</p>
                  <a 
                    href="/veydra-whitepaper---strategic-simulation-for-the-enterprise.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-gray-900 hover:text-gray-700"
                  >
                    Click here if download doesn't start →
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Article Content */}
          <div className="lg:col-span-8">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Whitepaper</span>
            <h1 className="text-3xl md:text-4xl font-medium text-gray-900 mt-2 mb-4 leading-tight tracking-tight">
              Strategic Simulation for the Enterprise
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Dynamic Decision Modeling with the ERP Data Backbone
            </p>

            <div className="prose prose-gray max-w-none">
              <p className="text-gray-600 leading-relaxed mb-4">
                Most enterprise analytics answer "what happened." But strategy teams often need 
                to explore "what if"—before committing budget, headcount, or capital.
              </p>
              
              <p className="text-gray-600 leading-relaxed mb-6">
                This whitepaper walks through how Veydra connects to your existing data stack 
                and uses simulation to help leaders test ideas safely—without touching production systems.
              </p>

              <div className="p-5 bg-gray-100 border-l-4 border-gray-900 mb-6">
                <p className="text-gray-800 font-medium">
                  Your ERP tells you what happened. Simulation helps you think through what's next.
                </p>
              </div>

              <p className="text-gray-600 leading-relaxed">
                Inside: how the architecture works, a few practical examples, and why this 
                approach complements (rather than replaces) the forecasting you already do.
              </p>
            </div>
          </div>
        </div>
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default WhitepaperStrategicSimulation;
